import {
  ChatActionType,
  isWeb,
  MessageActionValue,
  MessageEnvelope,
  MessageTagData,
  MessageType,
} from '@zonofi/common';
import {
  ChannelTypeEnum,
  dispatch,
  ErrorVariants,
  getChannelById,
  getWorkspaceId,
  MultiToastType,
  showToast,
  store,
  updateActionToastData,
  updateChatActionDrawer,
  updateSelectedMessage,
} from '@zonofi/seller-rtk-store';
import { isOwnMessage } from '../functions';
import { MessageActionsData } from '../components';
import Clipboard from '@react-native-clipboard/clipboard';
import { useParseFile } from './parse-file';
import { i18nService } from '@zonofi/i18n';

const handleCopy = async (text: string) => {
  if (isWeb()) {
    navigator.clipboard.writeText(text);
  } else {
    Clipboard.setString(text);
  }
};

interface MsgActionToast {
  message: string;
  title: string;
  status: 'success' | 'danger';
}

const showToastMessage = (toast: MsgActionToast) => {
  const { message, status, title } = toast;

  if (isWeb()) {
    dispatch(
      updateActionToastData({
        visibility: true,
        description: message,
        title: title,
        type:
          status === 'success' ? MultiToastType.SUCCESS : MultiToastType.ERROR,
      })
    );
  } else {
    dispatch(
      showToast({
        canDisplay: true,
        message: message,
        responseStatus: status?.toUpperCase(),
        type:
          status === 'success' ? ErrorVariants.SUCCESS : ErrorVariants.DANGER,
      })
    );
  }
};

export const EMOJI_LIST = isWeb()
  ? ['1f44d', '2764-fe0f', '1f602', '1f632']
  : ['1f44d', '2764-fe0f', '1f606', '1f62e', '1f632', '1f62f'];

const getMessageTag = (
  actionTags?: MessageActionValue,
  messageTags?: MessageTagData[]
): MessageTagData | null => {
  const latestTagKey = Object.entries(actionTags || {})
    .map(([key, tagActions]) => ({
      key,
      timetoken: Math.max(
        0,
        ...tagActions.map((action) => Number(action.actionTimetoken))
      ),
    }))
    .sort((a, b) => Number(b.timetoken) - Number(a.timetoken))[0]?.key;

  if (latestTagKey) {
    try {
      return JSON.parse(latestTagKey) as MessageTagData;
    } catch (error) {
      console.warn('Failed to parse message action tag:', latestTagKey, error);
    }
  }

  if (Array.isArray(messageTags) && messageTags.length) {
    return messageTags[0];
  }

  return null;
};

export const useGetMessageActionsMap = (envelope: MessageEnvelope) => {
  const {
    message,
    actions,
    channel,
    uuid = '',
    publisher = '',
  } = envelope || {};
  const { text, type, sType, sId, fileUrl } = message || {};
  const media = message?.media ?? [];
  const messageType = type === MessageType.ISSUE ? sType || type : type;
  const currentChannel = getChannelById(channel)(store.getState());
  const workspaceId = getWorkspaceId(store.getState());

  const { type: channelType, sellerWorkspaceId } = currentChannel || {};

  const isOwn = isOwnMessage(sId || uuid || publisher, currentChannel);

  const editedText = (
    Object.entries(actions?.['updated'] || {}).pop() || []
  ).shift() as string;
  const msgThreads = Object.values(actions?.['thread'] || {});
  const hasThread = msgThreads.length > 0;
  const messageTag = getMessageTag(actions?.['tags'], message?.tags);

  const { onParseFile, fileParseLoading } = useParseFile(
    sellerWorkspaceId || workspaceId
  );

  const fileSource = fileUrl || media[0]?.path || media[0]?.cUrl || '';
  const contentType = media[0]?.cntType
    ? media[0]?.cntType?.includes('/')
      ? media[0]?.cntType
      : media[0]?.cntType === 'pdf'
        ? 'application/pdf'
        : `image/${media[0]?.cntType}`
    : '';

  const onParseFileSuccess = (text: string) => {
    handleCopy(text);
    showToastMessage({
      title: i18nService.t('chats:FILE_PARSED'),
      message: i18nService.t('chats:FILE_PARSED_SUCCESS'),
      status: 'success',
    });
  };

  const hasTags =
    (messageTag &&
      (messageTag?.tag !== 'personal' || messageTag?.sub_tag !== 'null')) ||
    false;

  const msgActions: MessageActionsData = {
    reply: {
      title: i18nService.t('chats:REPLY'),
      icon: 'Reply',
      condition: channelType !== ChannelTypeEnum.PRINCIPAL,
      onPress: () =>
        dispatch(updateSelectedMessage({ action: 'reply', envelope })),
    },
    forward: {
      title: i18nService.t('chats:FORWARD'),
      icon: 'Forward',
      condition: messageType === MessageType.TEXT,
      onPress: () =>
        dispatch(updateSelectedMessage({ action: 'forward', envelope })),
    },
    addTag: {
      title: i18nService.t('chats:ADD_TAG'),
      icon: 'Tag',
      condition: !isOwn && !hasTags,
      onPress: () => {
        if (isWeb()) {
          dispatch(
            updateChatActionDrawer({
              open: true,
              type: ChatActionType.ADD_TAGS,
              msgEnvolope: envelope,
            })
          );
        } else {
          dispatch(updateSelectedMessage({ action: 'modify_tags', envelope }));
        }
      },
    },
    modifyTag: {
      title: i18nService.t('chats:MODIFY_TAGS'),
      icon: 'Tag',
      condition: hasTags && !isOwn,
      onPress: () => {
        if (isWeb()) {
          dispatch(
            updateChatActionDrawer({
              open: true,
              type: ChatActionType.TAG_MANAGEMENT,
              msgEnvolope: envelope,
            })
          );
        } else {
          dispatch(updateSelectedMessage({ action: 'modify_tags', envelope }));
        }
      },
    },
    issue: {
      title: i18nService.t('chats:ISSUE'),
      icon: 'Issue',
      condition:
        channelType !== ChannelTypeEnum.ISSUETHREAD &&
        channelType === ChannelTypeEnum.CUSTOMERTHREAD &&
        !hasThread,
      onPress: () =>
        dispatch(updateSelectedMessage({ action: 'issue', envelope })),
    },
    copy: {
      title: i18nService.t('chats:COPY'),
      icon: 'Copy',
      condition:
        Boolean(editedText || text) && (editedText || text)?.length !== 0,
      onPress: () =>
        handleCopy(editedText || text || '').then(() => {
          showToastMessage({
            title: i18nService.t('chats:MESSAGE_COPIED'),
            message: i18nService.t('chats:MESSAGE_COPIED_SUCCESS'),
            status: 'success',
          });
        }),
    },
    parse: {
      title: i18nService.t('chats:PARSE'),
      icon: fileParseLoading ? 'Spinner' : 'Copy',
      condition: false,
      // isArray(media) &&
      // media.some(
      //   (x) =>
      //     x.cntType?.includes('image') ||
      //     x.cntType?.includes('pdf') ||
      //     ImageFileExtensions.includes(x.cntType)
      // ),
      onPress: () => onParseFile(fileSource, contentType, onParseFileSuccess),
    },
    info: {
      title: i18nService.t('chats:MESSAGE_INFO'),
      icon: 'InfoIcon',
      condition: true,
      onPress: () =>
        dispatch(
          updateChatActionDrawer({
            open: true,
            type: ChatActionType.MESSAGE_INFO,
            msgEnvolope: envelope,
          })
        ),
    },
    delete: {
      title: i18nService.t('chats:DELETE'),
      icon: 'Delete',
      condition:
        isOwn &&
        msgThreads.length === 0 &&
        !(
          [
            ChannelTypeEnum.CUSTOMERTHREAD,
            ChannelTypeEnum.PRINCIPAL,
          ] as string[]
        ).includes(channelType),
      onPress: () =>
        dispatch(updateSelectedMessage({ action: 'delete', envelope })),
    },
  };

  return { msgActions, actionsLoading: fileParseLoading, messageTag };
};
